import { ArrowUpRightIcon } from "@phosphor-icons/react";
import { startCase } from "lodash";
import moment from "moment-timezone";

import { Badge } from "@alpacahq/ui/old";
import type { TableColumn } from "@alpacahq/ui/old/datagrid";

import type {
  CryptoAsset,
  CryptoWhitelistedAddress,
} from "~/v2/api/rest/cryptoWallet";
import { CopyButton } from "~/v2/components/common";
import { AssetChainCell } from "../presentation/AssetChainCell";
import {
  getWhitelistStatusBadgeProps,
  resolveAssetChainCell,
  transformAddress,
} from "../util";

export const createWhitelistColumns = ({
  assetMap,
  isTRCEnabled,
  onUpdateClicked,
  onDetailsClicked,
}: {
  assetMap: Record<string, CryptoAsset>;
  isTRCEnabled: boolean;
  onUpdateClicked: (address: CryptoWhitelistedAddress) => void;
  onDetailsClicked: (address: CryptoWhitelistedAddress) => void;
}): TableColumn<CryptoWhitelistedAddress>[] => [
  {
    key: "asset",
    name: "Asset / Chain",
    cellRenderer: ({ asset, chain }) => (
      <AssetChainCell {...resolveAssetChainCell({ asset, chain, assetMap })} />
    ),
  },
  {
    key: "address",
    name: "Address",
    cellRenderer: ({ address }) => (
      <div className="flex items-center gap-1">
        <span className="font-mono text-gray-700 text-sm">
          {transformAddress(address)}
        </span>
        <CopyButton value={address} ariaLabel="Copy address" />
      </div>
    ),
  },
  {
    key: "status",
    name: "Status",
    cellRenderer: (row) => {
      if (isTRCEnabled && !row.travel_rule_info) {
        return <Badge variant="v2/red">Update</Badge>;
      }
      return (
        <Badge {...getWhitelistStatusBadgeProps(row.status)}>
          {startCase(row?.status?.toLowerCase() ?? "")}
        </Badge>
      );
    },
  },
  {
    key: "created_at",
    name: "Date Added",
    cellRenderer: ({ created_at }) => (
      <div className="flex flex-col items-start justify-center gap-1">
        <div className="font-mono text-gray-700 text-sm">
          {moment(created_at).format("MM/DD/YYYY")}
        </div>
        <div className="font-mono text-gray-500 text-xs">
          {moment(created_at).tz("America/New_York").format("hh:mma")} ET
        </div>
      </div>
    ),
  },
  {
    key: "action",
    name: "Action",
    cellRenderer: (row) => {
      if (isTRCEnabled && !row.travel_rule_info) {
        return (
          <button
            type="button"
            onClick={() => onUpdateClicked(row)}
            className="inline-flex items-center gap-1 font-medium text-blue-500 text-sm hover:text-blue-400"
          >
            Update
            <ArrowUpRightIcon size={16} />
          </button>
        );
      }
      return (
        <button
          type="button"
          onClick={() => onDetailsClicked(row)}
          className="inline-flex items-center gap-1 font-medium text-blue-500 text-sm hover:text-blue-400"
        >
          Details
        </button>
      );
    },
  },
];
